import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';

/** One delegated, lazy-loaded dialog. Product purchase logic stays in Horizon. */
export class SnoozeQuickView extends DialogComponent {
  #listeners;
  #request;
  #generation = 0;
  #opener;
  #selection = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    document.addEventListener('click', this.#openFromCard, { signal });
    document.addEventListener('keydown', this.#trapFocus, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#closed, { signal });
    // Keep selections scoped to this dialog, including when it lives inside a section in editor previews.
    this.addEventListener(StandardEvents.productSelect, this.#selected, { signal });
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#cartUpdated, { signal });
  }

  disconnectedCallback() {
    this.#closed();
    this.#listeners?.abort();
    super.disconnectedCallback();
  }

  #openFromCard = async (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-snooze-quick-view]');
    if (!(button instanceof HTMLButtonElement) || this.refs.dialog.open || this.#opener) return;
    event.preventDefault();
    const card = button.closest('product-card');
    const url = new URL(card?.productPageUrl || button.dataset.productUrl, location.origin);
    if (url.origin !== location.origin) return;
    this.#opener = button;
    this.querySelector('#snooze-quick-view-title').textContent = button.dataset.productTitle;
    const selectedVariant = card?.getSelectedVariantId?.();
    if (selectedVariant) url.searchParams.set('variant', selectedVariant);
    this.querySelector('[data-quick-view-details]').href = url.href;
    this.querySelector('[data-quick-view-content]').replaceChildren();
    this.querySelector('[data-quick-view-availability]').hidden = true;
    this.#status(this.dataset.loading);
    this.showDialog();
    this.#request?.abort();
    this.#request = new AbortController();
    const generation = ++this.#generation;
    try {
      const response = await fetch(url, { signal: this.#request.signal });
      if (!response.ok) throw new Error('Product request failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (generation !== this.#generation) return;
      const grid = html.querySelector('[data-product-grid-content]');
      // Fail safely for alternate product templates without a native purchase form.
      if (!grid || !grid.querySelector('product-price')) throw new Error('Product template unsupported');
      this.prepareProductDocument(html);
      this.querySelector('[data-quick-view-content]').replaceChildren(grid);
      this.querySelector('#snooze-quick-view-title').textContent = grid.dataset.quickViewTitle || button.dataset.productTitle;
      this.#availability(grid.dataset.quickViewAvailable === 'true');
      this.#status(grid.dataset.quickViewRestricted === 'true' ? this.dataset.restriction : '');
    } catch (error) {
      if (error.name !== 'AbortError' && generation === this.#generation) {
        console.warn('[snooze-quick-view] Product could not be displayed:', error.message);
        this.#status(this.dataset.failure);
      }
    }
  };

  /** Also called by the native picker before it morphs a fetched variant response.
   * Namespace IDs and their references to isolate forms/radios from the background PDP.
   * Keep data-block-id unchanged: native price matching uses that server-provided key.
   * @param {Document | Element} html
   */
  prepareProductDocument(html) {
    const grid = html.querySelector('[data-product-grid-content]');
    if (!grid) return;
    // Full-page grid modifiers depend on PDP gutters/subgrid; the modal owns its layout.
    grid.classList.remove('product-information--media-left', 'product-information--media-right',
      'product-information__grid--half', 'product-information__grid--limit-details');
    const gallery = grid.querySelector('media-gallery');
    const firstImage = gallery?.querySelector('slideshow-slide[aria-hidden="false"] img, slideshow-slide:not([hidden]) img');
    if (firstImage) {
      firstImage.setAttribute('loading', 'eager');
      firstImage.setAttribute('fetchpriority', 'high');
      firstImage.setAttribute('sizes', '(min-width: 750px) min(50vw, 600px), 100vw');
    }
    grid.querySelectorAll('[id]').forEach((node) => {
      const id = node.getAttribute('id');
      if (!id.startsWith('snooze-qv-')) node.setAttribute('id', `snooze-qv-${id}`);
    });
    for (const attribute of ['for', 'form', 'aria-labelledby', 'aria-describedby', 'aria-controls']) {
      grid.querySelectorAll(`[${attribute}]`).forEach((node) => {
        node.setAttribute(attribute, node.getAttribute(attribute).split(/\s+/).map((id) => id.startsWith('snooze-qv-') ? id : `snooze-qv-${id}`).join(' '));
      });
    }
    grid.querySelectorAll('a[href^="#"]').forEach((link) => {
      if (!link.hash.startsWith('#snooze-qv-')) link.hash = `snooze-qv-${link.hash.slice(1)}`;
    });
    grid.querySelectorAll('*').forEach((node) => {
      for (const attribute of node.attributes) {
        if (attribute.name.startsWith('on:') && /^#(?!snooze-qv-)/.test(attribute.value)) {
          attribute.value = attribute.value.replace(/^#/, '#snooze-qv-');
        }
      }
    });
    grid.querySelectorAll('input[type="radio"]').forEach((input) => {
      if (!input.name.startsWith('snooze-qv-')) input.name = `snooze-qv-${input.name}`;
    });
    // Subscription widgets are not initialized in dynamically loaded HTML. Never
    // silently submit an incomplete subscription; keep variants/prices and the PDP link.
    if (grid.dataset.quickViewRestricted === 'true') {
      grid.querySelectorAll('product-form-component').forEach((form) => form.remove());
      this.querySelectorAll('product-form-component').forEach((form) => form.remove());
    }
    // Accelerated checkout and zoom dialogs require separate runtime initialization.
    // Standard native Add to Cart and the inline gallery remain fully functional.
    grid.querySelectorAll('[ref="acceleratedCheckoutButtonContainer"], zoom-dialog').forEach((node) => node.remove());
    grid.querySelectorAll('[on\\:click]').forEach((node) => {
      if (/zoom/.test(node.getAttribute('on:click'))) node.removeAttribute('on:click');
    });
    grid.querySelectorAll('.product-media-container__zoom-button').forEach((node) => node.remove());
    grid.querySelectorAll('script:not([type="application/json"]), [data-shopify-editor-block]').forEach((node) => {
      if (node.tagName === 'SCRIPT') node.remove();
      else node.removeAttribute('data-shopify-editor-block');
    });
  }

  #selected = (event) => {
    const selection = ++this.#selection;
    const generation = this.#generation;
    event.stopPropagation();
    event.promise.then(({ detail }) => {
      if (generation !== this.#generation || selection !== this.#selection || !detail?.html) return;
      const grid = detail.html.querySelector('[data-product-grid-content]');
      this.#availability(Boolean(detail.resource?.available));
      this.#status(grid?.dataset.quickViewRestricted === 'true' ? this.dataset.restriction : '');
      const picker = this.querySelector('variant-picker');
      const link = this.querySelector('[data-quick-view-details]');
      const url = new URL(detail.newProduct?.url || picker?.dataset.productUrl || link.href, location.origin);
      if (detail.resource?.id) url.searchParams.set('variant', detail.resource.id);
      else url.searchParams.delete('variant');
      link.href = url.href;
      if (grid?.dataset.quickViewTitle) this.querySelector('#snooze-quick-view-title').textContent = grid.dataset.quickViewTitle;
    }).catch((error) => {
      if (generation === this.#generation && selection === this.#selection && error.name !== 'AbortError') this.#status(this.dataset.failure);
    });
  };

  #cartUpdated = (event) => {
    const generation = this.#generation;
    event.promise?.then(({ detail }) => {
      if (generation === this.#generation && detail?.didError === false && this.refs.dialog.open) this.closeDialog();
    }).catch(() => {});
  };

  #trapFocus = (event) => {
    if (event.key !== 'Tab' || !this.refs.dialog.open) return;
    const elements = [...this.refs.dialog.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]')]
      .filter((node) => node.getClientRects().length && !node.closest('[hidden], [inert]'));
    const first = elements[0];
    const last = elements.at(-1);
    const active = document.activeElement;
    if (!first) return;
    if (!this.refs.dialog.contains(active) || (!event.shiftKey && active === last) || (event.shiftKey && active === first)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  };

  #availability(available) {
    const node = this.querySelector('[data-quick-view-availability]');
    node.hidden = false;
    node.textContent = available ? this.dataset.available : this.dataset.unavailable;
  }

  #status(message) {
    const node = this.querySelector('[data-quick-view-status]');
    node.textContent = message;
    node.hidden = !message;
    this.querySelector('[data-quick-view-content]').setAttribute('aria-busy', String(message === this.dataset.loading));
  }

  #closed = () => {
    ++this.#generation;
    ++this.#selection;
    this.#request?.abort();
    this.querySelector('[data-quick-view-content]')?.replaceChildren();
    if (this.#opener?.isConnected) this.#opener.focus({ preventScroll: true });
    this.#opener = null;
  };
}

if (!customElements.get('snooze-quick-view')) customElements.define('snooze-quick-view', SnoozeQuickView);
